import { useState, useEffect } from 'react';
import styles from './PasswordPromptModal.module.css';

interface PasswordPromptModalProps {
  show: boolean;
  onHide: () => void;
  onPasswordSubmit: (password: string) => void;
}

const PasswordPromptModal = ({ show, onHide, onPasswordSubmit }: PasswordPromptModalProps) => {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (!show) {
      setPassword('');
      setShowPassword(false);
    }
  }, [show]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (password.trim()) {
      onPasswordSubmit(password);
      onHide();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSubmit(e);
    }
  };

  if (!show) return null;

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <div className={styles.background} />

        <div className={styles.content}>
          <div className={styles.header}>
            <div className={styles.headerLeft}>
              <div className={styles.icon}>🔒</div>
              <div>
                <h2 className={styles.title}>Private Lobby</h2>
                <p className={styles.subtitle}>Enter password to join</p>
              </div>
            </div>

            <button className={styles.closeButton} onClick={onHide} aria-label="Close">
              ✕
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className={styles.formGroup}>
              <label className={styles.label}>Lobby Password</label>
              <div className={styles.inputWrapper}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Enter lobby password..."
                  autoFocus
                  className={styles.input}
                />
                <button
                  type="button"
                  className={styles.togglePassword}
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? '🙈' : '👁️'}
                </button>
              </div>
            </div>

            <div className={styles.notice}>
              <div className={styles.noticeIcon}>⚠️</div>
              <div>
                <div className={styles.noticeTitle}>Private Lobby Access</div>
                <div className={styles.noticeText}>
                  This lobby is password protected. Only users with the correct password can join.
                </div>
              </div>
            </div>

            <div className={styles.actions}>
              <button type="button" className={styles.cancelButton} onClick={onHide}>
                Cancel
              </button>
              <button
                type="submit"
                disabled={!password.trim()}
                className={`${styles.submitButton} ${password.trim() ? styles.submitButtonActive : styles.submitButtonDisabled}`}
              >
                🔓 Join Lobby
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PasswordPromptModal;
